import { createElement as rc, useState } from 'react';
import styled from '../styled';
import webOnlyStyles from '../webOnlyStyles';
import fromTheme from '../fromTheme';

let Label = styled.label`
    position: relative;
    height: ${({ size = 'sm', multistate = false }) =>
        fromTheme('switch', 'track', 'height', multistate ? 'multistate' : 'standard', size)};
    width: ${({ size = 'sm', multistate = false }) =>
        fromTheme('switch', 'track', 'width', multistate ? 'multistate' : 'standard', size)};
    padding: ${fromTheme('switch', 'track', 'padding')};
`;
Label = webOnlyStyles(Label)`
    display: inline-block;
`;

const Track = styled.span`
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ disabled, theme, value }) => {
        const switchState = getSwitchState(value);
        if (disabled) {
            return theme.switch.track.color.disabled;
        }
        switch (switchState) {
            case 'unchecked':
                return theme.switch.track.color.unchecked;
            case 'checked':
                return theme.switch.track.color.checked;
            default:
                // If the switch is in the neutral state, use the intermediate color
                return theme.switch.track.color.intermediate;
        }
    }};

    -webkit-transition: 0.4s;
    transition: 0.4s;
    border-radius: ${fromTheme('switch', 'track', 'borderRadius')};
    border: ${({ theme }) =>
        theme.selectedTheme === 'gl2025'
            ? `${theme.switch.track.border.width}px solid ${theme.switch.track.border.color}`
            : 'unset'};
    height: ${({ size = 'sm', multistate = false }) =>
        fromTheme('switch', 'track', 'height', multistate ? 'multistate' : 'standard', size)};
    width: ${({ size = 'sm', multistate = false }) =>
        fromTheme('switch', 'track', 'width', multistate ? 'multistate' : 'standard', size)};
    // todo: wasting too much time trying to get this to show up correctly.
    //  Once the padding and stuff is fixed and it looks right, uncomment the 'focused' stuff to allow to it show up
    box-shadow: ${({ theme /*, focused*/ }) =>
        /* focused || */ theme.selectedTheme !== 'gl2025' ? fromTheme('switch', 'track', 'boxShadow') : 'unset'};

    &:before {
        position: absolute;
        content: '';
        height: ${({ size = 'sm' }) => fromTheme('switch', 'thumb', 'height', size)};
        width: ${({ size = 'sm' }) => fromTheme('switch', 'thumb', 'width', size)};
        left: ${({ size = 'sm' }) => fromTheme('switch', 'thumb', 'position', size, 'left')};
        bottom: ${({ size = 'sm' }) => fromTheme('switch', 'thumb', 'position', size, 'bottom')};
        background-color: ${({ disabled, theme, value }) => {
            if (disabled) {
                return theme.switch.thumb.color.disabled;
            }
            // todo gl2025 these are the same color so ternary not needed
            return value ? theme.switch.thumb.color.checked : theme.switch.thumb.color.unchecked;
        }};
        -webkit-transition: 0.4s;
        transition: 0.4s;
        border-radius: 50%;
        transform: ${({ value, theme, size, multistate }) => {
            const switchState = getSwitchState(value);
            switch (switchState) {
                case 'unchecked':
                    return `translateX(${theme.switch.thumb.transform[multistate ? 'multistate' : 'standard'][size].unchecked}px)`;
                case 'checked':
                    return `translateX(${theme.switch.thumb.transform[multistate ? 'multistate' : 'standard'][size].checked}px)`;
                default:
                    // If the switch is in the neutral state, move the toggle to the middle of the track
                    return `translateX(${theme.switch.thumb.transform[multistate ? 'multistate' : 'standard'][size].intermediate}px)`;
            }
        }};
    }
`;

const Input = styled.input.attrs(() => ({
    type: 'checkbox'
}))`
    opacity: 0;
    width: 0;
    height: 0;
    &:checked {
        background-color: #2196f3;
    }
    &:focus {
        box-shadow: 0 0 1px #2196f3;
    }
`;

export default function Switch(props) {
    const {
        value,
        onClick,
        className,
        disabled,
        name,
        onBlur: _onBlur,
        onFocus: _onFocus,
        size = 'sm',
        multistate = false
    } = props;
    const onChange = e => onClick(e.target.checked);

    // we have to keep track of whether we're focused and pass it to the Track
    // since spans aren't focusable so we can't use pure CSS (&:focus)
    const [focused, setFocused] = useState(false);

    const onFocus = () => {
        setFocused(true);
        _onFocus?.();
    };

    const onBlur = () => {
        setFocused(false);
        _onBlur?.();
    };

    // We pass in a string when the toggle is in a neutral state, but we don't want that string to be interpreted as a checked value
    // So, if the value is not a boolean, default to false.
    const checked = typeof value === 'boolean' ? value : false;

    // prettier-ignore
    return rc(Label, { className, size, multistate },
        rc(Input, { name, checked, onChange, disabled, onBlur, onFocus, role: 'switch' }),
        rc(Track, { disabled, value, size, multistate, focused }) //"Thumb" is rendered by the `:before`
    );
}

/**
 * Get the state of the switch based on the toggle value.
 * Used to determine the color and transform of the switch.
 *
 * @param {boolean} value
 * @returns {'checked' | 'unchecked' | 'neutral'}
 */
const getSwitchState = value => {
    if (value === false) {
        return 'unchecked';
    } else if (value === true) {
        return 'checked';
    }
    return 'neutral';
};
